// Runtime configuration loaded from /config.json before the app boots.
//
// Replaces the build-time VITE_* env vars for the four
// deployment-specific values that differ between environments
// (homeserver URL, console URL, chat public URL, Sentry DSN).
// The same Docker image works in any environment — swap config.json
// at deploy time via ConfigMap mount or volume bind.
//
// Load order:
//   - Standalone shell (main.tsx): calls loadConfig() before render.
//     Fetches /config.json served by nginx (prod) or Vite dev server
//     (dev — from public/config.json).
//   - Embed consumers: never call loadConfig(). Host provides values
//     via EmbeddableChatProvider props.
//   - Tests: never call loadConfig(). vitest.config.ts seeds VITE_*
//     env vars; env.ts lazy gates fall through to import.meta.env.

export interface AppConfig {
  matrixHomeserverUrl: string;
  consoleUrl: string;
  chatPublicUrl: string;
  sentryDsn: string;
  meetOrigins: string[];
  bookingMeetUrl: string;
  hookshotWidgetUrl: string;
  hookshotBotUserId: string;
}

let config: AppConfig | null = null;

/**
 * Fetch /config.json and cache the result. Idempotent — repeated
 * calls return the cached config. Falls back to empty strings on
 * fetch failure (file:// protocol, embed context, test runner).
 */
export async function loadConfig(): Promise<AppConfig> {
  if (config) return config;
  const fallbackMeetOrigins = parseMeetOriginsCsv(import.meta.env.VITE_MEET_ORIGINS);

  try {
    const res = await fetch("/config.json");
    if (res.ok) {
      const json: unknown = await res.json();
      if (json && typeof json === "object") {
        const obj = json as Record<string, unknown>;
        config = {
          matrixHomeserverUrl:
            typeof obj.matrixHomeserverUrl === "string" ? obj.matrixHomeserverUrl : "",
          consoleUrl: typeof obj.consoleUrl === "string" ? obj.consoleUrl : "",
          chatPublicUrl: typeof obj.chatPublicUrl === "string" ? obj.chatPublicUrl : "",
          sentryDsn: typeof obj.sentryDsn === "string" ? obj.sentryDsn : "",
          meetOrigins: normaliseMeetOrigins(obj.meetOrigins, fallbackMeetOrigins),
          bookingMeetUrl:
            typeof obj.bookingMeetUrl === "string"
              ? obj.bookingMeetUrl
              : (import.meta.env.VITE_BOOKING_MEET_URL ?? ""),
          hookshotWidgetUrl:
            typeof obj.hookshotWidgetUrl === "string"
              ? obj.hookshotWidgetUrl
              : (import.meta.env.VITE_HOOKSHOT_WIDGET_URL ?? ""),
          hookshotBotUserId:
            typeof obj.hookshotBotUserId === "string"
              ? obj.hookshotBotUserId
              : (import.meta.env.VITE_HOOKSHOT_BOT_USER_ID ?? ""),
        };
        return config;
      }
    }
  } catch {
    // Fetch fails in test / embed / file:// contexts — fall through.
  }

  config = {
    matrixHomeserverUrl: "",
    consoleUrl: "",
    chatPublicUrl: "",
    sentryDsn: "",
    meetOrigins: fallbackMeetOrigins,
    bookingMeetUrl: import.meta.env.VITE_BOOKING_MEET_URL ?? "",
    hookshotWidgetUrl: import.meta.env.VITE_HOOKSHOT_WIDGET_URL ?? "",
    hookshotBotUserId: import.meta.env.VITE_HOOKSHOT_BOT_USER_ID ?? "",
  };
  return config;
}

function parseMeetOriginsCsv(value: string | undefined): string[] {
  if (!value) return [];
  return value
    .split(",")
    .map((entry) => entry.trim().toLowerCase())
    .filter((entry) => entry.length > 0);
}

function normaliseMeetOrigins(value: unknown, fallback: string[]): string[] {
  if (!Array.isArray(value)) return fallback;
  return value.flatMap((entry) => {
    if (typeof entry !== "string") return [];
    const trimmed = entry.trim();
    if (!trimmed) return [];
    const lowered = trimmed.toLowerCase();
    if (trimmed !== lowered) {
      console.warn("meetOrigins entries must be lowercase; normalising", trimmed);
    }
    return [lowered];
  });
}

/**
 * Read the cached config synchronously. Returns null when
 * loadConfig() has not been called (embed, test, storybook).
 * Callers that need a value should fall back to import.meta.env.
 */
export function getLoadedConfig(): AppConfig | null {
  return config;
}

/** Test hook: clear cached config so loadConfig() re-fetches. */
export function __resetConfigForTests(): void {
  config = null;
}
